import { useCallback, useEffect, useRef, useState } from 'react';

import { BackendErrorCode, Email, ShareLinkId, sleep, trackError } from 'backend-client-web';
import {
  ClientCoreErrorCode,
  Login,
  Unprotect,
  UnprotectedValue,
  openShareLink,
  previewShareLink,
  recursivelyTransformUnprotectedValues,
} from 'client-core';
import { FallbackComponent, LoginShareLinkView, ShareLinkState } from 'client-web-sdk';
import { DomainError } from 'domain-errors';

import getBackendClient, { getWebappUrl } from './servers';

/** An unknown id (backend) and a malformed link (client-core) both show the invalid link page. */
function isInvalidShareLink(e: unknown) {
  return (
    e instanceof DomainError &&
    (e.code === BackendErrorCode.SHARE_LINK_NOT_FOUND ||
      e.code === ClientCoreErrorCode.InvalidShareLink)
  );
}

export default function App() {
  const [shareLinkState, setShareLinkState] = useState<ShareLinkState>({ state: 'loading' });
  const [error, setError] = useState<unknown>();

  const displayError = useCallback((e: unknown) => {
    trackError(e);
    setError(e);
  }, []);

  const previewPromiseRef = useRef<
    Promise<{
      login: Login;
      shareLinkId: ShareLinkId;
      senderEmail: Email;
      organizationName?: string;
    }>
  >();

  useEffect(() => {
    if (shareLinkState.state !== 'loading') {
      return;
    }

    (async () => {
      try {
        if (!previewPromiseRef.current) {
          previewPromiseRef.current = previewShareLink(getBackendClient(), window.location.href);
        }
        const result = await previewPromiseRef.current;
        setShareLinkState({
          state: 'preview',
          login: result.login,
          senderEmail: result.senderEmail,
          organizationName: result.organizationName,
        });
      } catch (e) {
        if (isInvalidShareLink(e)) {
          setShareLinkState({ state: 'error' });
          return;
        }
        throw e;
      }
    })().catch(displayError);
  }, [displayError, shareLinkState.state]);

  // Opening a share link consumes it: the backend wipes the protected secret on the first call.
  // Memoized: a second call would get SHARE_LINK_NOT_FOUND and replace the shown login with the
  // error page.
  const unprotectPromiseRef = useRef<
    Promise<{
      login: Unprotect<Login, UnprotectedValue>;
      senderEmail: Email;
      organizationName?: string;
      shareLinkId: ShareLinkId;
    }>
  >();

  const unprotect = useCallback(async () => {
    if (shareLinkState.state !== 'preview') {
      return;
    }

    try {
      const delayPromise = sleep(500);
      if (!unprotectPromiseRef.current) {
        unprotectPromiseRef.current = openShareLink(getBackendClient(), window.location.href);
      }
      const result = await unprotectPromiseRef.current;

      // Wait at least 500ms to convey we are doing crypto
      await delayPromise;
      setShareLinkState({
        state: 'unprotected',
        login: recursivelyTransformUnprotectedValues(result.login, (upv) => upv.unencrypted),
        senderEmail: result.senderEmail,
        organizationName: result.organizationName,
      });
    } catch (e) {
      if (isInvalidShareLink(e)) {
        setShareLinkState({ state: 'error' });
        return;
      }
      throw e;
    }
  }, [shareLinkState.state]);

  if (error) {
    return <FallbackComponent error={error} />;
  }

  return (
    <LoginShareLinkView
      shareLinkState={shareLinkState}
      openShareLink={unprotect}
      showingUnlockOverlay={false}
      displayError={displayError}
      renderStartLink={({ className, children }) => (
        <a href={`${getWebappUrl()}/start`} className={className}>
          {children}
        </a>
      )}
    />
  );
}
